import { useState } from "react";
import { Link } from "react-router-dom";
import { Button, ModalDelete } from "expano-components";

import { Main, PageLoader, Error } from "components";
import useGet from "hooks/useGet";
import useDelete from "hooks/useDelete";

import { ReactComponent as ProductIcon } from "icons/product.svg";

import BasicData from "./components/BasicData";
import DangerousData from "./components/DangerousData";
import VatRates from "./components/VatRates";
import ChannelProductGroups from "./components/ChannelProductGroups";
import CountryKeywords from "./components/CountryKeywords";
import Products from "./components/Products";
import DocumentProductGroups from "./components/DocumentProductGroups";

const ProductGroupEdit = ({
  match: {
    params: { id },
  },
  history,
}) => {
  const [show_delete_modal, setShowDeleteModal] = useState(false);

  const {
    is_loading,
    is_error,
    error,
    data: product_group,
    refetch,
  } = useGet({
    query: ["product_group", id],
    path: `/product_groups/${id}`,
    context: "ui_show",
  });

  const { is_deleting, _delete } = useDelete({
    path: `/product_groups/${id}`,
    onSuccess: () => history.push("/product_groups"),
  });

  if (is_loading) {
    return <PageLoader />;
  }

  if (is_error) {
    return <Error message={error} />;
  }

  return (
    <Main
      page={{
        name: product_group?.name,
        icon: <ProductIcon />,
        breadcrumbs: [
          {
            name: "Produkty grupowe",
            path: "/product_groups",
          },
          { name: product_group?.name },
        ],
        buttons: (
          <>
            <Link
              className="btn btn-sm btn-blue"
              to={`/products_manage/product_groups/${product_group?.id}/statuses`}
            >
              <span>Statusy</span>
            </Link>
            <Button
              type="delete"
              text="Usuń produkt grupowy"
              disabled={is_deleting}
              onClick={() => setShowDeleteModal(true)}
            />
          </>
        ),
      }}
    >
      {show_delete_modal && (
        <ModalDelete
          handleDelete={_delete}
          handleClose={() => setShowDeleteModal(false)}
          name={`produkt grupowy ${product_group?.name}?`}
        />
      )}
      <div className="dashboard-wrapper-flex">
        <div className="column-half">
          <BasicData
            product_group_id={id}
            kind={product_group?.kind}
            refetch={refetch}
            ai_meta={{
              ai_cn_confidence: product_group?.ai_cn_confidence,
              ai_cn_justification: product_group?.ai_cn_justification,
              ai_cn_source: product_group?.ai_cn_source,
            }}
            initialValues={{
              name: product_group?.name,
              sku: product_group?.sku,
              root_taxon_id: product_group?.root_taxon?.id,
              producer_id: product_group?.producer?.id,
              brand_id: product_group?.brand?.id,
              is_dangerous: product_group?.is_dangerous,
              cn_code: product_group?.cn_code,
              country_of_origin: product_group?.country_of_origin,
            }}
          />

          <DangerousData
            is_dangerous={product_group?.is_dangerous}
            product_group_id={id}
            refetch={refetch}
            initialValues={{
              is_dangerous: product_group?.is_dangerous,
              safety_warnings: product_group?.safety_warnings,
              possible_risks: product_group?.possible_risks,
              instructions_for_use: product_group?.instructions_for_use,
            }}
          />
          <DocumentProductGroups
            product_group_id={id}
            data={product_group?.document_product_groups}
            refetch={refetch}
          />

          <VatRates
            data={product_group?.product_group_vat_rates}
            product_group_id={id}
            refetch={refetch}
          />
        </div>
        <div className="column-half">
          {[
            "Allegro::Platform",
            "Amazon::Platform",
            "Ebay::Platform",
            "Wsf::Platform",
            "Erli::Platform",
            "Emag::Platform",
            "Custom::Platform",
          ].map((platform_type) => {
            const data = product_group?.channel_product_groups?.filter(
              ({ channel: { platform } }) => platform?.type === platform_type,
            );

            return (
              <ChannelProductGroups
                key={platform_type}
                platform_type={platform_type}
                data={data}
                product_group_id={id}
                refetch={refetch}
              />
            );
          })}
          <CountryKeywords
            product_group_id={id}
            data={product_group?.product_group_country_keywords}
            refetch={refetch}
          />
          <Products data={product_group.products} />
        </div>
      </div>
    </Main>
  );
};

export default ProductGroupEdit;
